<script setup lang="ts">
import { computed } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { LucideChevronRight } from 'lucide-vue-next'

import {
  EntityOfferHomePagePrimaryCardCard,
  EntityOfferHomePagePrimaryCardCredit,
  EntityOfferHomePagePrimaryCardDeposit
} from '~/components/entity/offer/home-page'

import { WidgetHomePageBestOffersSkeleton } from '~/components/widget/best-offers'
import type { IUiHomePageBestOffersProps } from './types'

const props = withDefaults(defineProps<IUiHomePageBestOffersProps>(), {
  isLoading: false,
  error: null,
  offers: null
})

const emit = defineEmits<{
  'update:activeTab': [value: string]
}>()

const activeTabIndex = computed(() => {
  return parseInt(props.activeTab.replace('best-offer-tab-', '')) - 1
})

const getPrimaryCardComponent = computed(() => {
  const tabValue = props.tabs[activeTabIndex.value]?.value
  if (tabValue === 'deposits') return EntityOfferHomePagePrimaryCardDeposit
  if (tabValue === 'credits') return EntityOfferHomePagePrimaryCardCredit
  if (tabValue === 'cards') return EntityOfferHomePagePrimaryCardCard
  return null
})

const handleTabChange = (value: string) => {
  emit('update:activeTab', value)
}
</script>

<template>
  <Tabs :model-value="activeTab" class="" @update:model-value="handleTabChange">
    <header class="mb-5 flex flex-wrap items-center justify-between gap-1.5 sm:gap-3">
      <Heading tag="h2" class="mr-auto">{{ $t('widget.home_page.best_offers.heading') }}</Heading>
      <ScrollArea class="-mx-4 mt-3.5 pb-4">
        <TabsList class="mx-4">
          <TabsTrigger
            v-for="(tab, index) in tabs"
            :key="index"
            :value="`best-offer-tab-${index + 1}`"
          >
            {{ $t(tab.name) }}
          </TabsTrigger>
        </TabsList>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      <Button variant="link" size="auto" as-child>
        <NuxtLinkLocale to="/banks">
          {{ $t('common.view_all') }}
          <LucideChevronRight />
        </NuxtLinkLocale>
      </Button>
    </header>

    <TabsContent v-for="(tab, index) in tabs" :key="index" :value="`best-offer-tab-${index + 1}`">
      <article class="flex flex-col gap-8">
        <div v-if="isLoading">
          <div class="flex gap-4">
            <div v-for="i in 3" :key="i" class="w-[90%] flex-shrink-0 sm:w-[47%] lg:w-[31%]">
              <WidgetHomePageBestOffersSkeleton />
            </div>
          </div>
        </div>

        <div v-else-if="error" class="text-center text-red-500">
          {{ error }}
        </div>

        <div v-else-if="!offers || !offers.primary || !offers.primary.length" class="text-center">
          {{ $t('best_offers.no_data') }}
        </div>

        <div v-else>
          <Swiper :space-between="16" :slides-per-view="'auto'">
            <SwiperSlide
              v-for="(card, cardIndex) in offers.primary"
              :key="`${index}-${cardIndex}`"
              class="!w-[90%] sm:!w-[47%] lg:!w-[31%]"
            >
              <component :is="getPrimaryCardComponent" v-bind="card" />
            </SwiperSlide>
          </Swiper>
        </div>
      </article>
    </TabsContent>
  </Tabs>
</template>
